import { MenuItem2 } from '@blueprintjs/popover2';
import React from 'react';
import Icon from '@mdi/react';
import { IconSizeNew } from 'uf/ui/base/icon';
import { getIconByGeoType } from 'uf/ui/layers/LayerManager/LayerManagerList/LayerManagerList';
import { Menu } from '@blueprintjs/core';
import Fuse, { FuseResult, FuseResultMatch, RangeTuple } from 'fuse.js';
import { LayerReference } from 'uf-api-rtk/store/Api';

const LAYER_NAME_TRUNCATION = 25;

export interface SearchResultItem {
  allow_in_favorites: boolean;
  selected: boolean;
  layer: LayerReference;
}

export enum FuseOptionKeys {
  NAME = 'Name',
  TAG = 'Tag',
  CATEGORY = 'Category',
  DESCRIPTION = 'Description',
  COLUMN_NAME = 'Column Name',
  COLUMN_KEY = 'Column Key',
  COLUMN_DESCRIPTION = 'Column Description',
  SOURCE = 'Source',
}

interface ResultMatch extends FuseResultMatch {
  key: FuseOptionKeys;
}

type LayerSelectHandler = ({
  fullPath,
  categoryKey,
}: {
  fullPath: any;
  categoryKey: any;
}) => void;

interface Props {
  results: FuseResult<SearchResultItem>[];
  onClick: LayerSelectHandler;
  searchValue: string;
  minMatchCharLength: number;
}

export default function SearchMenu({
  results,
  onClick,
  searchValue,
  minMatchCharLength,
}: Props) {
  if (searchValue.length < minMatchCharLength) {
    return null;
  }
  if (searchValue.length >= minMatchCharLength && !results.length) {
    return (
      <Menu style={{ maxHeight: '50vh', overflowY: 'scroll' }}>
        <MenuItem2 disabled text="No matching layers" />
      </Menu>
    );
  }
  return (
    <Menu style={{ maxHeight: '50vh', overflowY: 'scroll' }}>
      {results.map(({ item, matches }) => (
        <SearchMenuItem
          key={item.layer.key}
          onClick={onClick}
          item={item}
          matches={matches as ResultMatch[]}
          searchValue={searchValue}
        />
      ))}
    </Menu>
  );
}

const getTruncatedHighlight = (match: ResultMatch, indices: RangeTuple) => {
  const [start, end] = indices;
  // if the substring doesn't start earlier than 10 characters in trim to the start index
  const hasLeadingText = start >= 10;
  const offset = hasLeadingText ? 5 : 0;
  const trimmedStart = hasLeadingText ? start - offset : 0;
  const matchSubstring = match.value.substring(trimmedStart);
  return {
    value: `${offset ? '...' : ''}${matchSubstring}`,
    key: match.key,
    indices: offset ? [[offset + 3, end - start + offset + 3]] : [[start, end]],
  } as ResultMatch;
};

interface ItemProps {
  item: SearchResultItem;
  matches: ResultMatch[];
  onClick: LayerSelectHandler;
  searchValue: string;
}

function SearchMenuItem({
  item: { layer },
  matches,
  onClick,
  searchValue,
}: ItemProps) {
  const {
    details: { name, display_hints: displayHints },
    full_path: fullPath,
  } = layer;
  const icon = getIconByGeoType(
    // @ts-ignore
    displayHints?.uf_geometry_type,
  );

  let nameMatch = matches?.find(({ key }) => key === FuseOptionKeys.NAME);
  // Truncate long layer names
  const isLongLayerName = name?.length > 50 && nameMatch?.indices?.[0][0] > 50;
  if (isLongLayerName) {
    const nameString = `...${name.substring(LAYER_NAME_TRUNCATION)}`;
    const [start, end] = nameMatch.indices[0];
    const newIndices = [
      [start - LAYER_NAME_TRUNCATION + 3, end - LAYER_NAME_TRUNCATION + 3],
    ] as RangeTuple[];
    nameMatch = {
      ...nameMatch,
      value: nameString,
      indices: newIndices,
    };
  } else {
    nameMatch = {
      ...nameMatch,
      value: name,
    };
  }

  // Filter out Name matches for secondary search matches
  let secondaryMatch = matches?.find(({ key }) => key !== FuseOptionKeys.NAME);
  const isList = secondaryMatch?.value?.includes('|');
  const isDescription = secondaryMatch?.key?.includes('Description');
  /**
   * If the secondary match is part of a list, we aggregated it originally
   * to avoid over-weighting the field. Below we split the results apart by
   * the delimiter and run another search to find the best match.
   */
  if (isList) {
    const secondaryValues = secondaryMatch.value.split('|');
    const fuse = new Fuse(secondaryValues, { includeMatches: true });
    const results = fuse.search(searchValue);
    // @ts-ignore
    secondaryMatch = { ...results?.[0]?.matches?.[0], key: secondaryMatch.key };
  }
  /**
   * If the secondary match is a description, we want to find the most
   * relevant match. Below we collect the matches into any array, by indices
   * and apply another search. We also apply a leading offset if the match
   * occurs later in the text.
   */
  if (isDescription) {
    const secondaryValue = secondaryMatch.value;
    const substrings = secondaryMatch?.indices?.reduce((acc, [start, end]) => {
      return [
        ...acc,
        {
          indices: [start, end],
          value: secondaryValue.substring(start, end),
        },
      ];
    }, []);
    const fuse = new Fuse(substrings, {
      keys: ['value'],
    });
    const results = substrings?.length ? fuse.search(searchValue) : [];
    if (results?.[0]) {
      const [start, end] = results?.[0]?.item?.indices;
      secondaryMatch = getTruncatedHighlight(secondaryMatch, [start, end]);
    }
  }
  return (
    <MenuItem2
      data-testid="search-menu-item"
      onClick={() =>
        // @ts-ignore
        onClick({ fullPath, categoryKey: displayHints?.layer_category })
      }
      icon={
        <Icon
          style={{ alignSelf: 'center' }}
          path={icon}
          size={IconSizeNew.SMALL}
        />
      }
      style={{ borderBottom: '1px solid #D9E2ED', alignItems: 'center' }}
      text={
        <>
          <Highlight
            text={nameMatch.value}
            indices={nameMatch?.indices as RangeTuple[]}
          />
          {secondaryMatch && (
            <p className="tw-text-md tw-text-gray-700 bp4-text-overflow-ellipsis">
              <strong>{secondaryMatch.key}: </strong>{' '}
              <Highlight
                indices={secondaryMatch?.indices as RangeTuple[]}
                text={secondaryMatch?.value}
              />
            </p>
          )}
        </>
      }
    />
  );
}

export function Highlight({
  text,
  indices,
}: {
  text: string;
  indices: undefined | RangeTuple[];
}) {
  if (!indices || !indices.length) {
    return <span>{text}</span>;
  }
  const substrings: { start: string; highlight: string; end: string }[] =
    indices.reduce(
      (parts, [highlightStart, highlightEnd], currentIndex, array) => {
        // Check for preceding highlight
        const startIndex = array[currentIndex - 1]
          ? /**
             * If there's a preceding highlight, set the start index
             * of the current highlight to right after the preceding
             * highlight ends.
             */
            array[currentIndex - 1]?.[1] + 1
          : 0;
        // Check for further highlight
        const hasFurtherHighlights = array[currentIndex + 1]?.[0];
        const start = text.substring(startIndex, highlightStart);
        const highlight = text.substring(highlightStart, highlightEnd + 1);
        /**
         * Don't terminate the string if there are further highlights, else
         * start the end string right after the highlight ends. This essentially
         * allows us to hide anything beyond the scope of the container behind
         * ellipses, if necessary, without a lot more calculations.
         */
        const end = hasFurtherHighlights
          ? null
          : text.substring(highlightEnd + 1);
        return [...parts, { start, highlight, end }];
      },
      [],
    );
  return (
    <span data-testid="highlighted-text">
      {substrings
        .filter(({ highlight }) => !!highlight)
        .map(({ start, highlight, end }) => (
          <span key={`${start}-${highlight}-${end}`}>
            {start}
            <mark
              data-testid="highlight"
              style={{
                backgroundColor: '#E6C5F0',
                padding: '3px 0',
                fontStyle: 'italic',
              }}>
              {highlight}
            </mark>
            {end && end}
          </span>
        ))}
    </span>
  );
}
